import AnchorText from './AnchorText';
import Row from './Grid/Row';
import InputCheckbox from './Input/InputCheckbox';
import React, {
  ChangeEventHandler,
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

const AgreementContainer = styled.div`
  ${Row} {
    display: block;
    padding-top: 16px;
    &.all-chk {
      border-bottom: 1px solid #e5e6e8;
      margin-top: 8px;
      padding: 16px 0;
    }

    .box {
      background: #f8f9fa;
      margin-top: 16px;
      padding: 24px;
    }
    .blit-list {
      font-size: 14px;
      line-height: 1.7;

      &::before {
        top: 10px;
      }

      & > li {
        margin-bottom: 10px;

        &:last-child {
          margin-bottom: 0;
        }
      }
      dl {
        display: flex;

        dt {
          width: 80px;
        }
        dd {
          width: calc(100% - 80px);
          font-weight: 100;
        }
      }
    }
  }
`;

const AgreementItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 5px 0;

  &.all-chk {
    border-bottom: 1px solid #e5e6e8;
    padding: 17px 0;
    margin-bottom: 10px;
  }
`;
const OpenButton = styled(AnchorText)`
  color: #828385;
`;

interface Policy {
  label: string;
  content1: string;
  content2: string;
  content3: string;
  content4: string;
  required?: boolean;
}

interface AgreementProps extends InputHTMLAttributes<HTMLInputElement> {
  policies: Policy[];
  labels?: {
    agreeAll: string;
    required: string;
    optional: string;
    more: string;
  };
  allChk?: boolean;
}

const DatabaseAgreement = forwardRef<HTMLInputElement, AgreementProps>(
  (
    {
      allChk,
      policies,
      labels = {
        agreeAll: `모두 동의합니다.`,
        required: `[필수]`,
        optional: `[선택]`,
        more: `보기`,
      },
      onBlur,
      ...props
    },
    ref,
  ) => {
    const { register, watch, setValue } = useForm();
    const [selected, setSelected] = useState<Policy>();

    const policyIds = useMemo(
      () => policies.map((_item, index) => `isAgreeYn${index}`),
      [policies],
    );
    const policyValues = watch(policyIds);
    // 모든 항목을 동의했는지 여부
    const allAgreed = useMemo(
      () =>
        Object.values(policyValues).reduce(
          (result, value) => result && value,
          true,
        ),
      [policyValues],
    );
    // 필수동의를 모두 동의했는지 여부
    const allRequiredAgreed = useMemo(
      () =>
        policies
          .filter((policy) => policy.required)
          .reduce(
            (result, policy) =>
              result && policyValues[`isAgreeYn${policies.indexOf(policy)}`],
            true,
          ),
      [policies, policyValues],
    );

    // 모두 동의된 상태로 계산되면, 전체동의 체크박스도 활성화
    useEffect(() => {
      setValue(`agreeAll`, allAgreed);
    }, [allAgreed, setValue]);

    const handleAgreeAll: ChangeEventHandler<HTMLInputElement> = (e) => {
      policies.forEach((_item, index) =>
        setValue(`isAgreeYn${index}`, e.target.checked),
      );
    };

    const openModal = (policy: Policy) => {
      setSelected(policy);
    };

    return (
      <AgreementContainer className="agreement-container" onBlur={onBlur}>
        {/* 상위 폼과 연결되는 readonly input */}
        <input
          {...props}
          ref={ref}
          type="checkbox"
          checked={allRequiredAgreed || false}
          readOnly
          style={{ height: 0, position: `absolute` }}
        />
        {allChk && (
          <AgreementItem className="all-chk">
            <InputCheckbox
              name="agreeAll"
              ref={register}
              onChange={handleAgreeAll}
              label={labels.agreeAll}
            />
          </AgreementItem>
        )}
        {policies.map((policy, index) => (
          <div key={policy.label}>
            <Row>
              <AgreementItem>
                <InputCheckbox
                  name={`isAgreeYn${index}`}
                  ref={register({ required: policy.required })}
                  label={`${
                    policy.required ? labels.required : labels.optional
                  } ${policy.label}`}
                />
              </AgreementItem>
              <div className="box">
                <ul className="blit-list">
                  <li>
                    <dl>
                      <dt>{policy.content1}</dt>
                      <dd>{policy.content2}</dd>
                    </dl>
                  </li>
                  <li>
                    <dl>
                      <dt>{policy.content3}</dt>
                      <dd>{policy.content4}</dd>
                    </dl>
                  </li>
                </ul>
              </div>
            </Row>
          </div>
        ))}
      </AgreementContainer>
    );
  },
);

export default DatabaseAgreement;
